import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "solarflow_sidebar_collapsed";

function readInitial() {
    try {
        return typeof window !== "undefined" && window.localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
        return false;
    }
}

/**
 * `useSidebarCollapse` — desktop-only manual collapse for the portal sidebars.
 *
 * The responsive breakpoints already collapse the sidebar to an icon rail
 * between 640–1023px automatically. This hook lets power users on ≥1024px
 * also pin the sidebar to icon-rail mode so table pages get more horizontal
 * space. State persists in localStorage across sessions.
 *
 * Returns `{ collapsed, toggle }`.
 */
export function useSidebarCollapse() {
    const [collapsed, setCollapsed] = useState(readInitial);

    const toggle = useCallback(() => {
        setCollapsed((prev) => {
            const next = !prev;
            try { window.localStorage.setItem(STORAGE_KEY, next ? "1" : "0"); } catch { /* ignore */ }
            return next;
        });
    }, []);

    // Keep state in sync across tabs (nice-to-have, essentially free).
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === STORAGE_KEY) setCollapsed(e.newValue === "1");
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, []);

    return { collapsed, toggle };
}
